/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import dayjs from 'dayjs'
import React from 'react'

import { font } from '../../css'
import { Flex } from '../../Layout'
import { media } from '../../Responsive'
import { SelectInput } from '../../ts-components/SelectInput'
import RangePicker from '../../ts-components/RangePicker'
import { useUrlManager } from 'src/hooks/useUrlManager'

const ALL_VALUE = 'all'

export const SortOrderButton = styled.button`
  ${font.body}
  height: 40px;
  background: #27303e;
  border: ${isCanlan ? 2 : 1}px solid #3f4753;
  border-radius: 4px;
  padding: 0 8px;
  color: white;

  &[disabled] {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    border-color: white;
  }

  &.sorted {
    border-color: white;
  }
`

export const InFrameSortOrderButton = styled.button`
  ${font.body}
  height: 40px;
  background: transparent;
  border: ${isCanlan ? 2 : 1}px solid #3f4753;
  border-radius: 4px;
  padding: 0 8px;
  color: ${colors.HEADER};

  &[disabled] {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    border-color: #3f4753;
  }

  &.sorted {
    border-color: ${colors.HEADER};
    border-width: 2px;
  }
`

interface Schedule {
  id: string
  name: string
  name_full: string
  parent?: { id: string }
}

interface Team {
  id: string
  name: string
  name_full?: string
  schedule_id: string
}

interface Filter {
  name: string
  schedules: Schedule[]
}

interface Facility {
  id: string
  name: string
}

interface Venue {
  id: string
  name: string
  name_full: string
  facilities: Facility[]
}

interface GameListFiltersProps {
  filters: Filter[]
  dateRange: {
    starts_at: string | null
    ends_at: string | null
  }
  team_id?: string
  onTeamChange: (teamId: string) => void
  onScheduleChange: (scheduleId: string, filterName: string) => void
  setDateRange: (range: {
    starts_at: string | null
    ends_at: string | null
  }) => void
  state: Record<string, string>
  setState: (state: Record<string, string>) => void
  teams: Team[]
  team: string
  sort: 'asc' | 'desc'
  setSort: (sort: 'asc' | 'desc') => void
  loading: boolean
  useDateRange?: boolean
  useGameOrder?: boolean
  children?: React.ReactNode
  isHidden?: boolean
  // Venue/Facility filter props
  venues?: Venue[]
  venue?: string
  onVenueChange?: (venueId: string) => void
  facility?: string
  onFacilityChange?: (facilityId: string) => void
  loadingVenues?: boolean
  useVenueFilter?: boolean
}

const GameListFilters: React.FC<GameListFiltersProps> = ({
  filters,
  dateRange,
  team_id,
  onTeamChange,
  onScheduleChange,
  setDateRange,
  state,
  setState,
  teams,
  team,
  sort,
  setSort,
  loading,
  useDateRange = false,
  useGameOrder = false,
  children,
  isHidden = false,
  // Venue/Facility filter props
  venues = [],
  venue = '',
  onVenueChange,
  facility = '',
  onFacilityChange,
  loadingVenues = false,
  useVenueFilter = false,
}) => {
  const { setParam } = useUrlManager()

  // Get facilities for the selected venue
  const selectedVenueFacilities = React.useMemo(() => {
    if (!venue || !venues?.length) {
      return []
    }
    const selectedVenue = venues.find((v) => v.id === venue)
    return selectedVenue?.facilities || []
  }, [venue, venues])

  // Determine if venue/facility dropdowns should be disabled (only when empty)
  const shouldDisableVenue = venues.length === 0
  const shouldDisableFacility = !venue || selectedVenueFacilities.length === 0

  if (
    isHidden ||
    (filters?.length === 0 && !useDateRange && !useGameOrder && !children)
  ) {
    return null
  }

  return (
    <Flex
      css={css`
        display: flex;
        gap: 16px;
        align-items: flex-end;
        justify-content: flex-start;
        flex-wrap: nowrap;
        border-bottom: 1px solid #3f4753;
        margin-bottom: 16px;
        padding-bottom: 16px;
        ${(media as any).mobile} {
          flex-wrap: wrap;
          padding: 0 16px 16px 16px;
        }
      `}
    >
      {filters.map(({ name, schedules = [] }, idx) => {
        let placeholderValue: string | undefined
        const rootScheduleIsSelected = schedules.find(
          (s) => s.id === team_id
        )?.id
        if (rootScheduleIsSelected) {
          placeholderValue = rootScheduleIsSelected
        }

        const nextFilter = filters[idx + 1]

        if (nextFilter) {
          const parentIdOfSelectedSchedule = nextFilter?.schedules?.find(
            (s) => {
              return s.id === state[nextFilter.name]
            }
          )?.parent?.id

          if (
            parentIdOfSelectedSchedule &&
            schedules.find((s) => s.id === parentIdOfSelectedSchedule)
          ) {
            placeholderValue = parentIdOfSelectedSchedule
          }
        }

        return (
          <div
            key={idx}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 0 1 250px;
              max-width: 250px !important;
              @media (max-width: 768px) {
                width: 100% !important;
                max-width: 100% !important;
                flex: 1 1 100% !important;
              }
            `}
          >
            <SelectInput
              label={name}
              disabled={loading}
              options={[
                {
                  label: `All ${name}${
                    name.charAt(name.length - 1) === 's' ? 'es' : 's'
                  }`,
                  value: ALL_VALUE,
                },
                ...schedules
                  .filter((schedule) => {
                    const previousFilters = filters.slice(0, idx)
                    let parentIdToCheck = schedule.parent?.id

                    for (let i = previousFilters.length - 1; i >= 0; i--) {
                      const previousFilter = previousFilters[i]
                      const previousFilterName = previousFilter.name

                      if (
                        state[previousFilterName] &&
                        state[previousFilterName].length > 0
                      ) {
                        return parentIdToCheck === state[previousFilterName]
                      } else {
                        const parentSchedule = previousFilter.schedules.find(
                          (s) => s.id === parentIdToCheck
                        )
                        if (parentSchedule) {
                          parentIdToCheck = parentSchedule.parent?.id
                        }
                      }
                    }

                    return true
                  })
                  .map((schedule) => ({
                    label: schedule.name,
                    value: schedule.id,
                  })),
              ]}
              name={name.toLowerCase()}
              value={
                state[name] ||
                (!Object.prototype.hasOwnProperty.call(state, name) &&
                placeholderValue
                  ? placeholderValue
                  : ALL_VALUE)
              }
              onChange={(scheduleId) => {
                // When ALL_VALUE is selected, treat as clearing
                const stateValue =
                  scheduleId === ALL_VALUE ? '' : scheduleId || ''
                const callbackValue =
                  scheduleId === ALL_VALUE ? '' : scheduleId || ''

                const filterThatChanged = filters.findIndex(
                  (filter) => filter.name === name
                )
                const filtersToReset = filters.slice(
                  filterThatChanged + 1,
                  filters.length
                )
                const keysToReset = filtersToReset.map((filter) => filter.name)

                let newState = { ...state, [name]: stateValue }
                keysToReset.forEach((keyName) => {
                  newState[keyName] = ''
                })

                setState(newState)
                if (name === 'Division') {
                  setParam('sn_division', stateValue)
                  setParam('sn_team', '')
                }
                if (name === 'Season') {
                  setParam('sn_schedule', stateValue)
                  setParam('sn_team', '')
                }
                onScheduleChange(callbackValue, name)
              }}
              fieldErrors={{}}
              customError={null}
              showSearch={false}
              backgroundColor='transparent'
              style={css`
                width: 100%;
              `}
            />
          </div>
        )
      })}

      {Array.isArray(teams) && teams.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 0 1 250px;
            @media (max-width: 768px) {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 100% !important;
            }
          `}
        >
          <SelectInput
            label='Team'
            disabled={loading}
            options={[
              { label: 'All Teams', value: ALL_VALUE },
              ...teams
                .filter((teamItem) => {
                  let parentIdToCheck = teamItem.schedule_id

                  for (let i = filters.length - 1; i >= 0; i--) {
                    const previousFilter = filters[i]
                    const previousFilterName = previousFilter.name

                    if (
                      state[previousFilterName] &&
                      state[previousFilterName].length > 0
                    ) {
                      return parentIdToCheck === state[previousFilterName]
                    } else {
                      const parentSchedule = previousFilter.schedules.find(
                        (s) => s.id === parentIdToCheck
                      )
                      if (parentSchedule) {
                        parentIdToCheck = parentSchedule.parent?.id || ''
                      }
                    }
                  }

                  return true
                })
                .map((teamItem) => ({
                  label: teamItem.name,
                  value: teamItem.id,
                })),
            ]}
            name='team'
            value={team || ALL_VALUE}
            onChange={(teamId) => {
              const stateValue = teamId === ALL_VALUE ? '' : teamId || ''
              setParam('sn_team', stateValue)
              onTeamChange(stateValue)
            }}
            fieldErrors={{}}
            customError={null}
            showSearch={true}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            backgroundColor='transparent'
            style={css`
              width: 100%;
            `}
          />
        </div>
      )}

      {useVenueFilter && venues.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 0 1 250px;
            max-width: 250px !important;
            opacity: ${loading || loadingVenues || shouldDisableVenue
              ? 0.5
              : 1};
            transition: opacity 0.2s ease;
            @media (max-width: 768px) {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 100% !important;
            }
          `}
        >
          <SelectInput
            label='Location'
            disabled={loading || loadingVenues || shouldDisableVenue}
            options={[
              { label: 'All Locations', value: ALL_VALUE },
              ...venues.map((v) => ({
                label: v.name_full || v.name,
                value: v.id,
              })),
            ]}
            name='location'
            value={venue || ALL_VALUE}
            onChange={(venueId) => {
              const value = venueId === ALL_VALUE ? '' : venueId || ''
              setParam('sn_venue', value)
              onVenueChange?.(value)
            }}
            fieldErrors={{}}
            customError={null}
            showSearch={true}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            backgroundColor='transparent'
            style={css`
              width: 100%;
            `}
          />
        </div>
      )}

      {useVenueFilter && venues.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 0 1 250px;
            max-width: 250px !important;
            opacity: ${loading || shouldDisableFacility ? 0.5 : 1};
            transition: opacity 0.2s ease;
            @media (max-width: 768px) {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 100% !important;
            }
          `}
        >
          <SelectInput
            label='Surface'
            disabled={loading || shouldDisableFacility}
            options={[
              { label: 'All Surfaces', value: ALL_VALUE },
              ...selectedVenueFacilities.map((f) => ({
                label: f.name,
                value: f.id,
              })),
            ]}
            name='surface'
            value={facility || ALL_VALUE}
            onChange={(facilityId) => {
              const value = facilityId === ALL_VALUE ? '' : facilityId || ''
              setParam('sn_facility', value)
              onFacilityChange?.(value)
            }}
            fieldErrors={{}}
            customError={null}
            showSearch={false}
            backgroundColor='transparent'
            style={css`
              width: 100%;
            `}
          />
        </div>
      )}

      {useDateRange && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 0 1 280px;
            @media (max-width: 768px) {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100%;
              flex: 1 1 100% !important;
            }
          `}
        >
          <RangePicker
            label='Date Range'
            allowClear={true}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const starts_at = dates[0].format('YYYY-MM-DD')
                const ends_at = dates[1].format('YYYY-MM-DD')

                setParam('sn_starts_at', starts_at)
                setParam('sn_ends_at', ends_at)

                setDateRange({
                  starts_at,
                  ends_at,
                })
              } else {
                // Clear date range
                setParam('sn_starts_at', '')
                setParam('sn_ends_at', '')

                setDateRange({
                  starts_at: null,
                  ends_at: null,
                })
              }
            }}
            starts_at={dateRange.starts_at ? dayjs(dateRange.starts_at) : null}
            ends_at={dateRange.ends_at ? dayjs(dateRange.ends_at) : null}
            fieldErrors={[]}
            name='dateRange'
            customError={null}
            format={{
              format: 'MM/DD/YYYY',
              type: 'mask',
            }}
            backgroundColor='transparent'
          />
        </div>
      )}

      {useGameOrder && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-self: stretch;
            flex: 0 1 200px;

            @media (max-width: 768px) {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 100% !important;
            }
          `}
        >
          <label
            css={css`
              font-size: 14px;
              font-weight: 400;
              color: ${colors.WHITE};
              padding-top: 6px;
            `}
          >
            Sort
          </label>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 2px;
              background-color: transparent;
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 4px;
              height: 40px;
              opacity: ${loading ? 0.5 : 1};
              transition: opacity 0.2s ease;
            `}
          >
            <button
              id='game-sort-asc'
              type='button'
              disabled={loading}
              onClick={() => {
                setParam('sn_sort', 'asc')
                setSort('asc')
              }}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${sort === 'asc'
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${sort === 'asc' ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${sort === 'asc'
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              Oldest
            </button>

            <button
              id='game-sort-desc'
              type='button'
              disabled={loading}
              onClick={() => {
                setParam('sn_sort', 'desc')
                setSort('desc')
              }}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${sort === 'desc'
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${sort === 'desc' ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${sort === 'desc'
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              Newest
            </button>
          </div>
        </div>
      )}
      {children}
    </Flex>
  )
}

export default GameListFilters
